import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table.tsx";
import { useModal } from "@/components/core/modals/use-modal";
import Pagination from "@/components/core/pagination.tsx";
import { Searchbar } from "@/components/core/searchbar.tsx";
import { Tabs, TabsBadge, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { articleStatuses } from "@/constants/article-statuses";
import { articlesQueryOptions } from "@/features/dashboard/api/queries/article-query";
import articleService from "@/features/dashboard/api/services/article-service";
import type { Article } from "@/features/dashboard/types/article";
import { useAuth } from "@/hooks/use-auth";
import { queryClient } from "@/query-client";
import { formatDate } from "@/utils/format-date.ts";
import { IconChevronDown, IconEye, IconPencil, IconPlus, IconReload, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/articles")({
	validateSearch: z.object({
		search: z.string().optional(),
		status: z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
		include: z.string().optional(),
		authors: z.string().optional(),
	}),
	preSearchFilters: [
		(search) => ({
			...search,
			status: articleStatuses.DRAFT,
			authors: "me",
		}),
	],
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		search: search.search,
		authors: search.authors,
		status: search.status,
	}),
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			articlesQueryOptions({
				...deps,
				// status: articleStatuses.DRAFT,
				// authors: "me",
			}),
		);
	},

	component: Page,
});

const columns: ColumnDef<Article>[] = [
	{
		id: "title",
		label: "Title",
		cell: ({ data }) => (
			<Link
				to="/articles/$articleId"
				params={{
					articleId: data.id as string,
				}}
				className="font-semibold text-blue-600 hover:text-blue-500"
			>
				{data.title}
			</Link>
		),
	},
	{
		id: "author",
		label: "Author",
		cell: ({ data }) => data.author,
	},
	{
		id: "published_at",
		label: "Date Published",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => formatDate(data.created_at) || "-",
	},
	{
		id: "updated_at",
		label: "Last Modified",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => formatDate(data.updated_at) || "",
	},
];

function Page() {
	const { user } = useAuth();
	const modal = useModal();
	const queryParams = new URLSearchParams(window.location.search);
	const [activeTab, setActiveTab] = useState(queryParams.get("status") || articleStatuses.DRAFT);
	const [activeAuthors, setActiveAuthors] = useState(queryParams.get("authors") || "me");

	const navigate = useNavigate();
	const search = Route.useSearch();
	const articlesQuery = useSuspenseQuery(
		articlesQueryOptions({
			...search,
			// status: activeTab,
			// authors: activeAuthors,
		}),
	);
	const articles = articlesQuery.data.data;
	const meta = articlesQuery.data.meta;
	const setUrl = async (status: string, authors: string) => {
		setActiveTab(status);
		setActiveAuthors(authors);
		await navigate({
			to: "/articles",
			search: (old) => ({
				...old,
				status: status,
				authors: authors,
			}),
			replace: true,
		});
	};

	const isSuperAdmin = Array.isArray(user?.roles) && user?.roles.some((role) => role.name === "Super Admin");

	// const articleDelete = useAricleDeleteMutation();

	const handleDelete = async (article: Article) => {
		modal.openModal({
			type: "confirm",
			title: "Confirm Action",
			variant: "red",
			description: "Are you sure you want to delete this item?",
			onConfirm: async () => {
				await articleService.deleteById(article.id as string);
				await queryClient.invalidateQueries({
					queryKey: ["articles"],
				});
				toast.success("Article successfully deleted.");
			},
		});
	};

	const handleRestore = async (article: Article) => {
		modal.openModal({
			type: "confirm",
			title: "Confirm Action",
			variant: "green",
			description: "Are you sure you want to restore this item?",
			onConfirm: async () => {
				await articleService.restoreById(article.id as string);
				await queryClient.invalidateQueries({
					queryKey: ["articles"],
				});
				toast.success("Article successfully restored.");
			},
		});
	};

	const handleDeletePermanently = async (article: Article) => {
		modal.openModal({
			type: "confirm",
			title: "Confirm Action",
			variant: "red",
			description: "Are you sure you want to permenantly delete this item?",
			onConfirm: async () => {
				await articleService.deleteByIdPermanently(article.id as string);
				await queryClient.invalidateQueries({
					queryKey: ["articles"],
				});
				toast.success("Article successfully deleted.");
			},
		});
	};

	return (
		<Content pageName="Articles">
			{/*<Header title="Articles" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Articles</span>
						<span className="text-base text-foreground/70">Write, publish, and manage your articles</span>
					</div>
					<Button
						onClick={() => {
							navigate({
								to: "/articles/create",
							});
						}}
						type="button"
					>
						<IconPlus size={16} stroke={2} />
						<span className="inline-flex">New Article</span>
					</Button>
				</div>

				<div className="flex flex-col w-full gap-4">
					<Tabs
						orientation="horizontal"
						defaultValue="me"
						onValueChange={(value) => {
							if (value === "me") {
								setActiveTab(articleStatuses.DRAFT);
								setActiveAuthors("me");
								setUrl(articleStatuses.DRAFT, "me");
							} else if (value === "others") {
								setActiveTab(isSuperAdmin ? articleStatuses.PENDING : articleStatuses.APPROVED);
								setActiveAuthors("others");
								setUrl(isSuperAdmin ? articleStatuses.PENDING : articleStatuses.APPROVED, "others");
							} else {
								setUrl(activeTab, value);
							}
						}}
					>
						<TabsList>
							<TabsTrigger value="me">My Articles</TabsTrigger>
							<TabsTrigger value="others">Others</TabsTrigger>
						</TabsList>
						<TabsContent value="me">
							<div className="flex flex-col w-full mt-4">
								<Tabs variant="compact" orientation="horizontal" defaultValue={articleStatuses.DRAFT} onValueChange={(value) => setUrl(value, "me")}>
									<TabsList>
										<TabsTrigger value={articleStatuses.DRAFT} count={meta.status_count?.[articleStatuses.DRAFT]}>
											{articleStatuses.DRAFT}
										</TabsTrigger>
										<TabsTrigger value={articleStatuses.PENDING} count={meta.status_count?.[articleStatuses.PENDING]}>
											{articleStatuses.PENDING}
										</TabsTrigger>

										<TabsTrigger value={articleStatuses.APPROVED} count={meta.status_count?.[articleStatuses.APPROVED]}>
											Approved
										</TabsTrigger>
										<TabsTrigger value={articleStatuses.REJECTED} count={meta.status_count?.[articleStatuses.REJECTED]}>
											{articleStatuses.REJECTED}
										</TabsTrigger>

										<TabsTrigger value={articleStatuses.UNPUBLISHED} count={meta.status_count?.[articleStatuses.UNPUBLISHED]}>
											{articleStatuses.UNPUBLISHED}
										</TabsTrigger>

										<TabsTrigger value={articleStatuses.DELETED} count={meta.status_count?.[articleStatuses.DELETED]}>
											Trash
										</TabsTrigger>
									</TabsList>
									<TabsContent value={activeTab}>
										<div className="flex flex-col w-full gap-4 mt-4">
											<DataTable
												key={"me"}
												showToolbar={true}
												data={articles}
												columns={columns}
												showBorder={true}
												compact={true}
												options={(item) => (
													<DropdownMenuGroup>
														{item.status === articleStatuses.DELETED ? (
															<>
																<DropdownMenuItem onClick={() => handleRestore(item)}>
																	<div className="flex flex-row items-center gap-1 p-1">
																		<IconReload size={16} stroke={2} className="text-green-600" />
																		<span className="translate-y-[-1px] leading-1 text-green-600">Restore</span>
																	</div>
																</DropdownMenuItem>
																<DropdownMenuItem onClick={() => handleDeletePermanently(item)}>
																	<div className="flex flex-row items-center gap-1 p-1 text-red-600">
																		<IconTrash size={16} stroke={2} />
																		<span className="translate-y-[-1px] leading-1">Delete Permanently</span>
																	</div>
																</DropdownMenuItem>
															</>
														) : (
															<>
																<DropdownMenuItem
																	onSelect={() => {
																		navigate({
																			to: `/articles/${item.id}`,
																		});
																	}}
																>
																	<div className="flex flex-row items-center gap-1 p-1">
																		<IconPencil size={16} stroke={2} />

																		<span className="translate-y-[-1px] leading-1">Edit</span>
																	</div>
																</DropdownMenuItem>

																<DropdownMenuItem onClick={() => handleDelete(item)}>
																	<div className="flex flex-row items-center gap-1 p-1 text-red-600">
																		<IconTrash size={16} stroke={2} />
																		<span className="translate-y-[-1px] leading-1">Delete</span>
																	</div>
																</DropdownMenuItem>
															</>
														)}
													</DropdownMenuGroup>
												)}
											/>

											<Pagination
												onClick={async (page) => {
													await navigate({
														to: "/articles",
														search: (old) => ({
															...old,
															page,
														}),
													});
												}}
												initialPage={meta.current_page}
												pageCount={meta.last_page}
											/>
										</div>
									</TabsContent>
								</Tabs>
							</div>
						</TabsContent>

						<TabsContent value="others">
							<div className="flex flex-col w-full mt-4">
								<Tabs
									variant="compact"
									orientation="horizontal"
									defaultValue={isSuperAdmin ? articleStatuses.PENDING : articleStatuses.APPROVED}
									onValueChange={(value) => setUrl(value, "others")}
								>
									{Array.isArray(user?.roles) &&
										user?.roles.map((role, index) => {
											if (role.name === "Super Admin") {
												return (
													<TabsList key={index}>
														<TabsTrigger value={articleStatuses.PENDING} count={meta.status_count?.[articleStatuses.PENDING]}>
															{articleStatuses.PENDING}
														</TabsTrigger>

														{/* <TabsTrigger
  														value={articleStatuses.APPROVED}
  														count={
  															meta.status_count?.[articleStatuses.APPROVED]
  														}
  													>
  														{articleStatuses.APPROVED}
  													</TabsTrigger> */}
														<TabsTrigger value={articleStatuses.APPROVED}>{articleStatuses.APPROVED}</TabsTrigger>

														<TabsTrigger value={articleStatuses.REJECTED} count={meta.status_count?.[articleStatuses.REJECTED]}>
															{articleStatuses.REJECTED}
														</TabsTrigger>

														<TabsTrigger value={articleStatuses.UNPUBLISHED} count={meta.status_count?.[articleStatuses.UNPUBLISHED]}>
															{articleStatuses.UNPUBLISHED}
														</TabsTrigger>
													</TabsList>
												);
											}
											return (
												<TabsList key={index}>
													<TabsTrigger value={articleStatuses.APPROVED} count={0}>
														{articleStatuses.APPROVED}
													</TabsTrigger>
												</TabsList>
											);
										})}
									<TabsContent value={activeTab}>
										<div className="flex flex-col w-full gap-4 mt-4">
											<DataTable
												key={"others"}
												showToolbar={true}
												data={articles}
												columns={columns}
												showBorder={true}
												compact={true}
												options={(article) => (
													<DropdownMenuGroup>
														<DropdownMenuItem
															onSelect={() => {
																navigate({
																	to: `/articles/${article.id}`,
																});
															}}
														>
															<div className="flex flex-row items-center gap-1 p-1">
																{user?.id === article.created_by ? <IconPencil size={16} stroke={2} /> : <IconEye size={16} stroke={2} />}
																<span className="translate-y-[-1px] leading-1">{user?.id === article.created_by ? "Edit" : "View"}</span>
															</div>
														</DropdownMenuItem>
													</DropdownMenuGroup>
												)}
											/>

											<Pagination
												onClick={async (page) => {
													console.log(articlesQuery);

													await navigate({
														to: "/articles",
														search: (old) => ({
															...old,
															status: activeTab,
															authors: activeAuthors,
															page,
														}),
													});
												}}
												initialPage={articlesQuery.data.meta.current_page}
												pageCount={articlesQuery.data.meta.last_page}
											/>
										</div>
									</TabsContent>
								</Tabs>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}
